import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    /* AppStyle (needed for demo render() in main.js) */
    appStyle: 'default',

    /* User */
    userName: null,
    userEmail: null,
    userAvatar: null,
    userRole: null,

    /* NavBar */
    isNavBarVisible: true,
    navBarColor: null,

    /* FooterBar */
    isFooterBarVisible: true,

    /* Aside */
    isAsideVisible: true,
    isAsideExpanded: false,
    isAsideMobileExpanded: false,
    asideActiveForcedKey: null,
    isAsideRightVisible: false,
    isAsideRightActive: false,

    /* Updates */
    hasUpdates: true,

    isCoordinator: false,
    isTeacher: false,
    isClient: false,
    clientId: null,
    teacherId: null,
    /* Overlay */
    isOverlayVisible: false,

    /* Layout */
    isLayoutFullPage: false,
    isLayoutBoxed: false,
    isLayoutAsideHidden: false,
    isLayoutMobile: false,

    /* Dark mode (available with AppLightDark.vue only) */
    isDarkModeActive: false,

    /* Iframe */
    isIframePreviewMode: false,

    /* ConfigBox */
    isConfigBoxVisible: true,

    /* Sample data (commonly used) */
    updates: []
  },
  mutations: {
    /* A fit-them-all commit */
    basic (state, payload) {
      state[payload.key] = payload.value
    },

    /* AppStyle */
    appStyle (state, payload) {
      state.appStyle = payload
    },

    /* User */
    user (state, payload) {
      if (payload.name) {
        state.userName = payload.name
      }
      if (payload.email) {
        state.userEmail = payload.email
      }
      if (payload.avatar) {
        state.userAvatar = payload.avatar
      }
      if (payload.clientId) {
        state.clientId = payload.clientId
      }
      if (payload.teacherId) {
        state.teacherId = payload.teacherId
      }
      if (payload.role) {
        state.userRole = payload.role
        if (state.userRole === 'Coordinator') {
          state.isCoordinator = true
        } else if (state.userRole === 'Teacher') {
          state.isTeacher = true
        } else if (state.userRole === 'Client') {
          state.isClient = true
        }
      }
    },

    /* Full Page mode */
    fullPage (state, payload) {
      state.isConfigBoxVisible = !payload
      state.isNavBarVisible = !payload
      state.isAsideVisible = !payload
      state.isFooterBarVisible = !payload
      state.isOverlayVisible = false
      state.isLayoutFullPage = payload
    },

    /* Aside Desktop Visibility */
    asideVisibilityToggle (state, payload) {
      state.isAsideVisible = payload
    },

    /* Aside Desktop State */
    asideStateToggle (state, payload = null) {
      const isExpand = payload !== null ? payload : !state.isAsideExpanded

      document.documentElement.classList[isExpand ? 'add' : 'remove']('has-aside-expanded')

      state.isAsideExpanded = isExpand
    },

    /* Aside Mobile State */
    asideMobileStateToggle (state, payload = null) {
      const isShow = payload !== null ? payload : !state.isAsideMobileExpanded

      document.documentElement.classList[isShow ? 'add' : 'remove']('has-aside-mobile-expanded')

      state.isAsideVisible = !state.isLayoutFullPage
      state.isAsideMobileExpanded = isShow
    },

    /* Aside Forced Active Key (when secondary submenu is open) */
    asideActiveForcedKeyToggle (state, payload) {
      state.asideActiveForcedKey = payload && payload.menuSecondaryKey ? payload.menuSecondaryKey : null
    },

    /* Aside Right */
    asideRightToggle (state, payload) {
      state.isAsideRightVisible = payload
      state.isAsideRightActive = payload
      state.hasUpdates = false
    },

    /* Overlay */
    overlayToggle (state, payload = null) {
      const setIsVisible = payload !== null ? payload : !state.isOverlayVisible

      if (!setIsVisible && state.isLayoutAsideHidden && (state.isAsideVisible || state.isAsideRightVisible)) {
        return
      }

      state.isOverlayVisible = setIsVisible

      document.documentElement.classList[setIsVisible ? 'add' : 'remove']('is-clipped')
    },

    /* Layouts */
    layoutBoxedToggle (state, payload = null) {
      const setIsBoxed = payload !== null ? payload : !state.isLayoutBoxed

      state.isLayoutAsideHidden = setIsBoxed
      state.isLayoutBoxed = setIsBoxed
      state.isAsideExpanded = setIsBoxed
      state.isAsideVisible = !setIsBoxed
      state.isAsideRightVisible = false
      state.isAsideRightActive = false

      document.documentElement.classList[setIsBoxed ? 'remove' : 'add']('has-aside-left', 'has-navbar-fixed-top')
      document.documentElement.classList[setIsBoxed ? 'add' : 'remove']('has-boxed-layout', 'has-aside-hidden-layout', 'has-aside-expanded')
    },

    layoutWideToggle (state, payload = null) {
      const setIsWide = payload !== null ? payload : !state.isLayoutBoxed

      state.isLayoutAsideHidden = setIsWide
      state.isAsideExpanded = setIsWide
      state.isAsideVisible = !setIsWide
      state.isAsideRightVisible = !setIsWide

      document.documentElement.classList[setIsWide ? 'remove' : 'add']('has-aside-left')
      document.documentElement.classList[setIsWide ? 'add' : 'remove']('has-aside-hidden-layout', 'has-aside-expanded')
    },

    layoutMobileToggle (state, payload) {
      state.isLayoutMobile = payload
    },

    /* Dark Mode */
    darkModeToggle (state, payload = null) {
      const setIsDark = payload !== null ? payload : !state.isDarkModeActive

      state.isDarkModeActive = setIsDark

      document.documentElement.classList[setIsDark ? 'add' : 'remove']('is-dark-mode-active')
    },

    /* Misc */
    setNavBarColor (state, payload) {
      state.navBarColor = payload
    },

    iframePreviewMode (state, payload) {
      state.isIframePreviewMode = payload
    },

    changeStatusForStudent (state, payload) {
      state.attendanceForUsersTable[payload.index].dates[payload.field.key] = payload.value
    }
  },
  actions: {
    asideCloseAll ({ commit }) {
      commit('asideVisibilityToggle', false)
      commit('asideRightToggle', false)
      commit('overlayToggle', false)
    },
    asideVisibilityToggle ({ commit, state }, payload = null) {
      const setIsVisible = payload !== null ? payload : !state.isAsideVisible

      commit('asideVisibilityToggle', setIsVisible)
      commit('overlayToggle', setIsVisible)
    },
    asideRightToggle ({ commit, state }, payload = null) {
      const isShow = payload !== null ? payload : !state.isAsideRightVisible

      commit('asideRightToggle', isShow)

      if (state.isLayoutAsideHidden) {
        commit('overlayToggle', isShow)
      }

      if (!state.isLayoutAsideHidden) {
        document.documentElement.classList[isShow ? 'add' : 'remove']('has-aside-right')
      }
    },
    layoutMobileToggle ({ commit, state }) {
      const isMobile = window.innerWidth < 1024

      commit('layoutMobileToggle', isMobile)

      document.documentElement.classList[isMobile && state.isIframePreviewMode ? 'add' : 'remove']('iframe-preview-mode')
    },
    toggleFullPage ({ commit }, payload) {
      commit('layoutBoxedToggle', false)
      commit('fullPage', payload)

      document.documentElement.classList.remove('is-clipped')

      if (payload) {
        document.documentElement.classList.remove('has-aside-left', 'has-navbar-fixed-top')
      }
    },
    fetch ({ commit }, payload) {
      axios
        .get(`/data-sources/${payload}.json`)
        .then((r) => {
          if (r.data && r.data.data) {
            commit('basic', {
              key: payload,
              value: r.data.data
            })
          }
        })
        .catch(error => {
          alert(error.message)
        })
    },
    handleStatusForStudent ({ commit }, payload) {
      commit('changeStatusForStudent', payload)
    }
  }
})

export default store

export function useStore () {
  return store
}
